import { DocumentFileList } from "@/common/components/DocumentFileList"
import { MultipleDocumentSoftUpload } from "@/common/components/MultipleDocumentSoftUpload"
import useBlobs from "@/common/hooks/queries/useBlobs"
import { ErrorsMapper } from "@/common/services/errorsMapper"
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl"
import { useCreatePrivateCompanyLeadGenericDocument } from "@/features/privateCompanyLeadsFunnel/hooks/useCreatePrivateCompanyLeadGenericDocument"
import { useDeletePrivateCompanyLeadGenericDocument } from "@/features/privateCompanyLeadsFunnel/hooks/useDeletePrivateCompanyLeadGenericDocument"
import usePrivateCompanyLead from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLead"
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema"
import { PrivateCompanyLeadData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Control } from "react-hook-form"

interface GenericDocumentsSectionProps {
  privateCompanyLeadId?: string
  generic_documents: PrivateCompanyLeadData['generic_documents']
  control: Control<PrivateCompanyLeadsFunnelUpdateValidationSchema, any>
  disabled?: boolean
}

export const GenericDocumentsSection = ({ privateCompanyLeadId, generic_documents, control, disabled }: GenericDocumentsSectionProps) => {
  const queryClient = useQueryClient()
  const [serverError, setServerError] = useState('')

  const { isFetching } = usePrivateCompanyLead(privateCompanyLeadId || '')

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs({
    onSuccess: (response) => {
      createPrivateCompanyLeadGenericDocument({ blob_id: response.data.data.id })
    },
  })

  const { mutate: createPrivateCompanyLeadGenericDocument, isPending: createPrivateCompanyLeadIsPending } = useCreatePrivateCompanyLeadGenericDocument({
    privateCompanyLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })
      setServerError('')
    },
    onError: (error) => {
      const transformedErrors = ErrorsMapper.default()(error?.response?.data.errors)

      !!transformedErrors.length && setServerError(transformedErrors.map((error) => error.generic_documents).join(', '))
    }
  })

  const { mutate: deletePrivateCompanyLeadGenericDocument, isPending: deletePrivateCompanyLeadIsPending } = useDeletePrivateCompanyLeadGenericDocument({
    privateCompanyLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[18px] font-bold">Anexos</p>
      {serverError && <p className="text-sm text-danger-soft">{serverError}</p>}
      {disabled ? (
        <DocumentFileList
          files={generic_documents?.map((document) => ({
            id: document.attachment_id,
            fileName: getFilenameFromUrl(document.url),
            currentFileUrl: document.url,
          })) || []}
        />
      ) : (
        <MultipleDocumentSoftUpload
          title="Anexe um arquivo"
          name="generic_documents"
          files={generic_documents}
          accept=".png, .jpg, .jpeg, .webp, .pdf, .doc, .docx, .xls, .xlsx"
          uploadBlob={uploadBlob}
          deletePrivateCompanyLeadGenericDocument={deletePrivateCompanyLeadGenericDocument}
          control={control}
          fieldName="generic_documents"
          disabled={uploadBlobIsPending || createPrivateCompanyLeadIsPending || deletePrivateCompanyLeadIsPending || isFetching}
        />
      )}
    </div>
  )
}
